import { useEffect, useState } from "react";
import { Lab, Membership, Unit, User } from "../../../backend/src/types";
import { userContext } from "../contexts/UserContext";
import { getAlertsCount } from "../fetch/AlertRequest";
import { getUnitList } from "../fetch/ReagentGroupRequest";
import { loadContextDeta } from "../fetch/UserRequest";
import { errorMessage } from "../function/errorMessage";

interface UserProviderProps {
  children: React.ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [userLabs, setUserLabs] = useState<Lab[]>([]);
  const [alertCount, setAlertCount] = useState<number>(0);

  useEffect(() => {
    callCheckSession();
  }, []);

  // 単位マスタは認証必須エンドポイントのため、ユーザー確定後に取得する
  useEffect(() => {
    if (!user) return;
    (async () => {
      const unitList = await getUnitList();
      setUnits(unitList);
    })();
  }, [user]);

  //セッションがすでに確立しているかどうかの確認
  async function callCheckSession() {
    try {
      setIsLoading(true);

      const sessionId = localStorage.getItem("sessionId");

      if (sessionId) {
        const result = await loadContextDeta({ sessionId: sessionId });

        if (result !== null) {
          setUser(result.user);
          setMemberships(result.memberships);
          setLabs(result.labs);

          const lab_idList = result.memberships
            .map((m) => m.lab_id)
            .filter((id): id is number => id !== null);

          const userLabList = result.labs.filter((lab) =>
            lab_idList.includes(lab.id),
          );

          const sortedLabList = Array.from(userLabList.values()).sort((a, b) =>
            a.name.localeCompare(b.name, "ja"),
          );

          setUserLabs(sortedLabList);

          const lab_ids = sortedLabList.map((lab) => lab.id);

          const alertCount = await getAlertsCount({
            tenant_id: result.user.tenant_id,
            lab_ids,
          });

          setAlertCount(alertCount);
        }
      }
    } catch (error) {
      errorMessage("セッションリクエストでエラーが発生しました", error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <userContext.Provider
      value={{
        user,
        setUser,
        memberships,
        setMemberships,
        units,
        setUnits,
        labs,
        setLabs,
        userLabs,
        setUserLabs,
        isLoading,
        alertCount,
        setAlertCount,
      }}
    >
      {children}
    </userContext.Provider>
  );
};
